import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInterceptor';

type UploadProfileImagePayload = {
  file: File;
};

type UploadProfileImageResult = {
  fileUrl: string;
  urls: string[];
  response: unknown;
};

type UploadProfileImageState = {
  loading: boolean;
  data: UploadProfileImageResult | null;
  error: boolean;
  errorMessage: string;
};

const initialState: UploadProfileImageState = {
  loading: false,
  data: null,
  error: false,
  errorMessage: '',
};

export const uploadProfileImage = createAsyncThunk<
  UploadProfileImageResult,
  UploadProfileImagePayload
>('uploadProfileImage', async ({ file }) => {
  if (!file) {
    throw new Error('Missing profile image for upload.');
  }

  const formData = new FormData();
  formData.append('file', file);

  const { data } = await axiosInstance.post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const fileUrl = data?.url || data?.file_url || data?.fileUrl;
  if (!fileUrl) {
    throw new Error('Upload failed: missing profile image URL.');
  }

  const urls = fileUrl ? [fileUrl] : [];
  return {
    fileUrl,
    urls,
    response: data,
  };
});

const uploadProfileImageSlice = createSlice({
  name: 'uploadProfileImage',
  initialState,
  reducers: {
    resetProfileImageUpload: (state) => {
      state.loading = false;
      state.data = null;
      state.error = false;
      state.errorMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadProfileImage.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(uploadProfileImage.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(uploadProfileImage.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Profile image upload failed.';
    });
  },
});

export const { resetProfileImageUpload } =
  uploadProfileImageSlice.actions;
export default uploadProfileImageSlice.reducer;
